import { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { dateFormat } from "../../helpers/utilities";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Tooltip, Button, Grid, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { toast } from 'react-toastify';
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import Select from 'react-select';
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import EndProject from "./EndProject";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import { matchesSearch } from "../../helpers/search";
import * as moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import "../Recruiter/recruiter.css"
import "../../assets/scss/tables.scss";

/* the columns the table shows, so a project is found by anything on its row */
const SEARCH_FIELDS = ['projectId', 'projectName', 'projectDescription', 'managerNames', 'status'];

/* Every manager code on a project, across all of its teams.

   `projectTeam[].managerIds` is exactly what the Team popup reads to fill its
   Lead/Manager box, so the table is drawing on the field that is known to hold
   the real codes rather than on a second, separate resolution. `managerCodes`
   is the same list flattened by the API; anything only it knows about is kept
   as well. */
const managerCodesOf = (project) => {
    const codes = [];

    const add = (value) => {
        const code = String(value || "").trim();
        if (code && !codes.includes(code)) codes.push(code);
    };

    (project.projectTeam || []).forEach((team) => (team.managerIds || []).forEach(add));
    (project.managerCodes || []).forEach(add);

    return codes;
};

/* A code becomes a name from the employee list this page already loads — the
   same list the popup matches against — falling back to whatever the API
   resolved server-side, and finally to the bare code, so a manager whose
   record has gone is still reported rather than quietly dropped. */
const managersOf = (project, employeesByCode = {}) => managerCodesOf(project).map((code) => {
    const fromList = employeesByCode[code];
    const fromApi = (project.projectManagers || []).find((m) => m && m.employeeCode === code);

    const employeeName = (fromList && fromList.employeeName)
        || (fromApi && fromApi.employeeName) || "";

    return {
        employeeCode: code,
        employeeName,
        designation: (fromList && fromList.designation)
            || (fromApi && fromApi.designation) || "",
        unresolved: !employeeName,
    };
});

/* An ObjectId opens with the seconds since the epoch at which it was made, so
   it dates a project even where createdDate cannot be trusted. */
const createdAt = (project) => {
    const id = String(project._id || "");
    return /^[0-9a-f]{8}/i.test(id) ? parseInt(id.substring(0, 8), 16) * 1000 : 0;
};

const ManageProjects = () => {

    const { fetchData } = useAxios();
    let dateOneMonthAgo=new Date();
    dateOneMonthAgo.setMonth(dateOneMonthAgo.getMonth() - 1);
    const [loadSkeleton, setLoadSkeleton] = useState(true);

    const [isLoad, setIsLoad] = useState(false);

    const [addProjectshow, setAddProjectshow] = useState(false);

    const [teamShow, setTeamShow] = useState(false);

    const [endProjectModal, setEndProjectModal] = useState(false);

    const [pageNumber, setPageNumber] = useState(1);

    /* fifteen rows to a page by default; 30 and 50 are the other choices */
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [search, setSearch] = useState("");

    const [projectPayload, setProjectPayload] = useState({
        projectId: "",
        projectName: "",
        projectStartDate: new Date(),
        projectEndDate: new Date(),
        projectDescription: ""
    })

    const [projectList, setProjectList] = useState([]);

    const [employeesList, setEmployeesList] = useState([]);

    const [projectTeam, setProjectTeam] = useState({
        projectName: "",
        teamName: "",
        projectID: "",
        managerID: "",
        teamsList: [],
        managerIds: [String]
    })

    const onChangeHandler = (e) => {
        setProjectPayload({
            ...projectPayload,
            [e.target.name]: e.target.value
        })
    }

    const addProjectsModel = () => setAddProjectshow(!addProjectshow);

    const teamModel = () => setTeamShow(!teamShow);

    const endProjectMod = () => setEndProjectModal(!endProjectModal)

    const teamMangerChange = async (value) => {
        let arrEmpCodes = [];
        for (var i = 0; i < value.length; i++) {
            arrEmpCodes.push(value[i].employeeCode);
        }
        try {
            setProjectTeam({
                ...projectTeam,
                managerIds: arrEmpCodes
            })
        } catch (err) {
        }
    }

    const teamMembersChange = async (value) => {
        try {            
            setProjectTeam({
                ...projectTeam,
                teamsList: value.map(x => {
                    return { employeeCode: x.employeeCode }
                })
            })
        } catch (err) {
        }
    }

    const getProjectList = async () => {
        setLoadSkeleton(true);
        const resp = await fetchData({ url: URLS.getProjectList, method: 'get' });
        if (resp.status === 200) {
            setProjectList(resp.data);
            setLoadSkeleton(false);
        }
    }

    const getEmployeesList = async () => {
        const resp = await fetchData({
            url: URLS.searchEmployees, method: 'post', body: {
                search: ""
            }
        });
        if (resp.status === 200) {
            setEmployeesList(resp.data);
        }
    }

    useEffect(() => {
        getProjectList();
        getEmployeesList();
        setIsLoad(true);
    }, [isLoad]);

    const getPageNo = (data) => {
        setPageNumber(data)
    }

    /* the employee list keyed by code, so each row is a lookup rather than a
       scan of the whole list per manager */
    const employeesByCode = useMemo(() => employeesList.reduce((acc, employee) => {
        if (employee && employee.employeeCode) acc[employee.employeeCode] = employee;
        return acc;
    }, {}), [employeesList]);

    /* The API already sorts this way, but the status it sorted by and the one
       printed below are worked out separately — so the table orders itself by
       the very value it shows, and the two can never disagree. */
    const rows = projectList
        .map((project) => {
            const managers = managersOf(project, employeesByCode);
            return {
                ...project,
                managers,
                managerNames: managers.map((m) => m.employeeName || m.employeeCode).join(', '),
                status: moment(project.projectEndDate).isBefore(new Date(), 'day') ? 'Completed' : 'Running',
                createdAt: createdAt(project),
            };
        })
        .sort((a, b) => {
            if (a.status !== b.status) return a.status === 'Running' ? -1 : 1;
            return b.createdAt - a.createdAt;
        });

    const searchResult = rows.filter((project) => matchesSearch(project, search, SEARCH_FIELDS));

    /* a narrower list can leave you past the last page */
    useEffect(() => { setPageNumber(1); }, [search]);

    return <div className='content wh-table manage-projects'>
        {/* title, then the search box in the middle of the bar, then the
            one action — the two outer cells share the leftover space so the
            box sits centred rather than wherever it happens to land */}
        <div className="wh-toolbar mp-toolbar">
            <h4 className='title'>Manage Projects</h4>

            <div className="mp-toolbar-search">
                <TextField
                    placeholder="Search project, ID, manager…"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="wh-search-field"
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: search
                            ? <InputAdornment position="end">
                                <IconButton size="small" className="wh-search-clear"
                                    aria-label="Clear search" onClick={() => setSearch("")}>
                                    <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                            : null,
                    }}
                />
            </div>

            <div className="mp-toolbar-actions">
                <Button type="button" variant="contained" onClick={(e) => {
                    setProjectPayload({
                        projectId: "",
                        projectName: "",
                        projectStartDate: new Date(),
                        projectEndDate: new Date(),
                        projectDescription: ""
                    })
                    addProjectsModel()
                }}>Add Project</Button>
            </div>
        </div>
        <div className="shadow-sm bg-white">
            <div className="card-body">
                <div className="table-responsive wh-table-shell">
                    {
                        loadSkeleton ? <SkeletonLoader /> : (searchResult.length > 0 ?
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th scope="col" className="col-text">Project ID</th>
                                        <th scope="col" className="col-text">Project Name</th>
                                        <th scope="col" className="col-text">Project Manager</th>
                                        <th scope="col" className="col-text">Start Date</th>
                                        <th scope="col" className="col-text">End Date</th>
                                        <th scope="col" className="col-text">Status</th>
                                        {/* stays put while the rest of the table scrolls */}
                                        <th scope="col" className="col-text wh-pin-1 wh-pin-edge">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResult.slice((pageNumber - 1) * rowsPerPage,
                                    pageNumber * rowsPerPage).map((project) =>{
                                        return <tr key={project.projectId}>
                                            <td className="row-text" scope="row">{project.projectId}</td>
                                            <td className="row-text">{project.projectName}</td>
                                            <td className="row-text">
                                                {project.managers.length
                                                    ? <span className="wh-pills wh-pills-narrow">
                                                        {project.managers.map((manager) => (
                                                            <Tooltip key={manager.employeeCode} placement="top" arrow
                                                                title={manager.unresolved
                                                                    ? `${manager.employeeCode} — no employee record found`
                                                                    : `${manager.employeeCode}${manager.designation ? ` · ${manager.designation}` : ''}`}>
                                                                <span className={`wh-pill${manager.unresolved ? ' wh-pill-quiet' : ''}`}>
                                                                    {manager.employeeName || manager.employeeCode}
                                                                </span>
                                                            </Tooltip>
                                                        ))}
                                                    </span>
                                                    : <span className="wh-cell-muted">Not assigned</span>}
                                            </td>
                                            <td className="row-text">{dateFormat(project.projectStartDate)}</td>
                                            <td className="row-text">{dateFormat(project.projectEndDate)}</td>
                                            <td>
                                                <span className={`wh-pill ${project.status === 'Completed' ? 'wh-pill-quiet' : 'wh-pill-good'}`}>
                                                    {project.status}
                                                </span>
                                            </td>
                                            <td className="row-text wh-pin-1 wh-pin-edge">
                                              <div className="wh-actions-row">
                                                <Tooltip title="Team Members" placement="top" arrow>
                                                    <Button type="button" variant="outlined" onClick={(e) => {
                                                        const obj = {
                                                            projectName: project.projectName,
                                                            teamName: "",
                                                            projectID: project.projectId,
                                                            managerID: "",
                                                            teamsList: [],
                                                            managerIds: []
                                                        }
                                                        if (project.projectTeam && project.projectTeam.length) {
                                                            obj.teamName = project.projectTeam[0].teamName;
                                                            obj.teamsList = project.projectTeam[0].teamsList;
                                                            obj.managerID = project.projectTeam[0].managerID;
                                                            obj.managerIds = project.projectTeam[0].managerIds
                                                        }
                                                        setProjectTeam({
                                                            ...obj,
                                                        })
                                                        teamModel()
                                                    }}>Team ({project.projectTeam ? project.projectTeam.length ? project.projectTeam[0].teamsList.length : 0 : 0})</Button></Tooltip>
                                                <Tooltip title="Edit" placement="top" arrow>
                                                    <Button type="button" variant="outlined" onClick={((e) => {
                                                    setProjectPayload({
                                                        projectId: project.projectId,
                                                        projectName: project.projectName,
                                                        projectStartDate: new Date(project.projectStartDate),
                                                        projectEndDate: new Date(project.projectEndDate),
                                                        projectDescription: project.projectDescription
                                                    })
                                                    addProjectsModel()
                                                })}>Edit</Button></Tooltip>
                                                <Button type="button" variant="outlined" className="wh-danger-btn" disabled={project.status === 'Completed'}
                                                onClick={() => {
                                                    setEndProjectModal(true)
                                                }}>End Project</Button>
                                              </div>
                                            </td>
                                        </tr>
                                    })}
                                </tbody>
                            </table>

                            : (search.trim()
                                ? <NoData title="Nothing matches that"
                                    message="Try a different project name, ID or manager." />
                                : <NoData />)
                        )
                    }
                        {searchResult.length > 0 ?
                        <PaginationGlobal count={Math.ceil(searchResult.length / rowsPerPage)}
                        pageNumber={pageNumber} func={getPageNo}
                        rowsPerPage={rowsPerPage} rowsPerPageOptions={[15, 30, 50]}
                        onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} /> : "" }

                </div>
            </div>
        </div>
        <div className='col-12'>
            <Modal show={addProjectshow} onHide={addProjectsModel} backdrop="static" size="lg" className="wh-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{projectPayload.projectId ? 'Update' : 'Add'} Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField id="outlined-basic" label="Project Name" variant="outlined" fullWidth onChange={onChangeHandler} value={projectPayload.projectName} name="projectName" required />
                        </Grid>

                        <Grid item xs={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label="Start Date"
                                    inputFormat="DD/MM/YYYY"
                                    value={projectPayload.projectStartDate}
                                    minDate={dateOneMonthAgo}
                                    onChange={(date) => {
                                        setProjectPayload({ ...projectPayload, projectStartDate: date })
                                    }}
                                    renderInput={(params) => <TextField {...params} fullWidth required
                                    />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label="End Date"
                                    inputFormat="DD/MM/YYYY"
                                    value={projectPayload.projectEndDate}
                                    minDate={moment().toDate()}
                                    onChange={(dt) =>
                                        {   
                                             setProjectPayload({ ...projectPayload, projectEndDate:dt})
                                        }
                                    }
                                    renderInput={(params) => <TextField {...params} fullWidth required
                                    />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField id="outlined-basic" rows={2} multiline label="Project Description" variant="outlined" onChange={onChangeHandler} value={projectPayload.projectDescription} name="projectDescription" fullWidth />
                        </Grid>
                    </Grid>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" className="me-3" onClick={addProjectsModel}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={async (e) => {
                        const resp = await fetchData({ url: URLS.manageProject, method: 'post', body: projectPayload });
                        if (resp.status === 200) {
                            addProjectsModel();
                            getProjectList();
                            toast.success(resp.message)
                        }
                        else{
                            toast.error(resp.message,'error')
                        }
                    }}
                        disabled={projectPayload.projectName && projectPayload.projectStartDate && projectPayload.projectEndDate ? false : true}
                    >
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={teamShow} onHide={teamModel} backdrop="static" className="wh-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{projectTeam.projectName} - {projectTeam.projectID} - Team</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='col-md-12 row'>
                        <div className='col-md-12'>
                            <label htmlFor="exampleFormControlInput1" className="form-label">Team Name</label>
                            <input type="text" value={projectTeam.teamName} name="teamName" onChange={(e) => {
                                setProjectTeam({
                                    ...projectTeam,
                                    teamName: e.target.value
                                })
                            }} className="form-control" id="exampleFormControlInput1" placeholder="Team Name" />
                        </div>
                        <div className='col-md-12 mt-2'>
                            <label htmlFor="exampleFormControlInput1" className="form-label">Lead/Manager</label>
                            <Select
                                isMulti={true}
                                value={projectTeam.managerIds ? employeesList.filter(x => projectTeam.managerIds.find(y => x.employeeCode === y)) : []}
                                options={employeesList.filter((x => !(projectTeam.teamsList.find(y => y.employeeCode === x.employeeCode))))}
                                onChange={teamMangerChange}
                            />
                        </div>
                        <div className='col-md-12 mt-2'>
                            <label htmlFor="exampleFormControlInput1" className="form-label">Team Members ({projectTeam.teamsList.length})</label>
                            <Select
                                isMulti
                                value={employeesList.filter(x => projectTeam.teamsList.find(y => x.employeeCode === y.employeeCode))}
                                options={employeesList.filter((x => !(projectTeam.managerIds.find(y=>x.employeeCode === y))))}                                
                                onChange={teamMembersChange} />
                        </div>


                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={teamModel} className="me-3">
                        Close
                    </Button>
                    <Button onClick={async (e) => {
                        const resp = await fetchData({ url: URLS.projectTeamManage, method: 'post', body: projectTeam });
                        if (resp.status === 200) {
                            teamModel();
                            getProjectList();
                            toast.success(resp.message)
                        }
                        else {
                            toast.error(resp.message, 'error')
                        }

                    }} variant="contained"
                     disabled={projectTeam.teamName ? false:true}
                    >
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={endProjectModal} onHide={endProjectModal} backdrop="static" size="md" className="wh-modal">
                <Modal.Header closeButton onClick={endProjectMod}>
                    <Modal.Title>End Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EndProject />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" className="me-3" onClick={endProjectMod}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={endProjectMod}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
           
        </div>
    </div>
}

export default ManageProjects